import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ListChecks } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { VendorConversation } from '../../../common/models/support.model';
import { CONVERSATION_STATUS_META } from '../support-meta';

dayjs.extend(relativeTime);

/** The vendor's conversations: status, subject, last message, unread. */
export function ConversationList({
  conversations,
  activeId,
  onOpen,
}: {
  conversations: VendorConversation[];
  activeId?: string;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <ul className="flex flex-col gap-1" data-testid="conversation-list">
      {conversations.map((c) => {
        const status = CONVERSATION_STATUS_META[c.status];
        const unread = c.unread > 0;
        return (
          <li key={c._id}>
            <button
              type="button"
              onClick={() => onOpen(c._id)}
              className={cn(
                'flex w-full flex-col gap-1 rounded-xl px-3 py-2.5 text-start transition-colors hover:bg-muted/70',
                activeId === c._id && 'bg-muted',
              )}
            >
              <span className="flex w-full items-center gap-2">
                <span className={cn('size-1.5 shrink-0 rounded-full', status.dot)} aria-hidden />
                <span className={cn('min-w-0 flex-1 truncate text-sm', unread ? 'font-medium' : '')}>{c.subject}</span>
                <span className="shrink-0 text-[11px] text-muted-foreground">{dayjs(c.lastMessageAt).fromNow(true)}</span>
              </span>
              <span className="flex w-full items-center gap-2 ps-3.5">
                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                  {c.lastMessageBy === 'support' ? `${t('SUPPORT.LIST.ARDEALY_PREFIX')} ` : ''}
                  {c.lastMessagePreview}
                </span>
                {c.tasks.length > 0 && (
                  <span className="flex shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground" title={t('SUPPORT.LIST.TASKS', { count: c.tasks.length })}>
                    <ListChecks className="size-3" />
                    {c.tasks.length}
                  </span>
                )}
                {unread && (
                  <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-medium text-background" data-testid="unread-count">
                    {c.unread}
                  </span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
